/**
 * Review, the mistake worklist, and the pronunciation clinic.
 *
 * Grades are batched behind a client-generated `batchId`, so an offline session replays
 * exactly once on reconnect (docs/07-tech-architecture.md §10).
 */

import type { RouteResponse } from '@saysooner/api-client';
import {
  DISPUTE_REASON_LABELS,
  DISPUTE_REASON_ORDER,
  type DisputeReason,
  sparklineSummary,
} from '@saysooner/core';
import {
  Banner,
  Button,
  EmptyState,
  ErrorState,
  MicButton,
  RemediationCard,
  Skeleton,
  Sparkline,
} from '@saysooner/ui';
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { noTracking, type Track } from '../app-context.js';
import { playMedia } from '../lib/media.js';
import type { WordAttempt } from '../lib/practise-word.js';

type Queue = RouteResponse<'getReviewQueue'>;
type Card = Queue['cards'][number];
type Mistakes = RouteResponse<'listMistakes'>;
type Clinic = RouteResponse<'getPronunciationClinic'>;

export interface PendingGrade {
  readonly cardId: string;
  readonly modality: Card['modality'];
  readonly correct: boolean;
  readonly elapsedMs: number;
  readonly hintUsed: boolean;
  readonly pronunciationScore?: number;
  readonly reviewedAt: number;
}

export interface ReviewSessionProps {
  readonly queue: Queue | null;
  readonly loading: boolean;
  readonly error: unknown;
  readonly offline: boolean;
  readonly allowSpoken: boolean;
  /** Called once per card; the host accumulates and submits in batches. */
  readonly onGrade: (grade: PendingGrade) => void;
  readonly track?: Track | undefined;
  readonly onFinish: (grades: readonly PendingGrade[]) => void;
  readonly onRetry: () => void;
  readonly onNavigate: (route: string) => void;
  /**
   * Reports the current item as wrong and advances.
   *
   * Optional so the screen tests that do not care can omit it; when it is absent the
   * control is not rendered rather than rendered dead, because a report button that
   * does nothing is the competitor behaviour this exists to beat.
   */
  readonly onReport?: ((itemId: string, reason: DisputeReason) => void) | undefined;
}

export function ReviewSession(props: ReviewSessionProps): JSX.Element {
  const track = props.track ?? noTracking;
  const sessionStartedAt = useRef(Date.now());
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [grades, setGrades] = useState<PendingGrade[]>([]);
  const [reporting, setReporting] = useState(false);
  // Items reported in this session. Held locally so the card leaves on the tap rather
  // than on the response: the whole point is that reporting visibly does something.
  const [reported, setReported] = useState<readonly string[]>([]);

  const cards = (props.queue?.cards ?? []).filter((entry) => !reported.includes(entry.itemId));
  const card = cards[index];

  const commit = (correct: boolean, pronunciationScore?: number): void => {
    if (card === undefined) return;
    const grade: PendingGrade = {
      cardId: card.cardId,
      modality: card.modality,
      correct,
      elapsedMs: Date.now() - shownAt,
      hintUsed,
      // Client timestamp preserves ordering when the batch replays after reconnect.
      reviewedAt: Date.now(),
      ...(pronunciationScore !== undefined ? { pronunciationScore } : {}),
    };

    props.onGrade(grade);
    // Per card, with the modality it was actually answered in: a card the learner
    // typed and a card they said are different exercises, and the scheduler treats
    // them differently, so the data has to be able to tell them apart.
    track('review_card', {
      item: card.cardId,
      modality: card.modality,
      grade: correct ? 'correct' : 'incorrect',
      ms: grade.elapsedMs,
    });

    const next = [...grades, grade];
    setGrades(next);
    setRevealed(false);
    setHintUsed(false);
    setShownAt(Date.now());

    if (index + 1 >= cards.length) {
      const correctCount = next.filter((entry) => entry.correct).length;
      track('review_session_complete', {
        count: next.length,
        accuracy: next.length === 0 ? 0 : Math.round((correctCount / next.length) * 100),
        duration_ms: Date.now() - sessionStartedAt.current,
      });
      props.onFinish(next);
    } else {
      setIndex(index + 1);
    }
  };

  if (props.loading) {
    return (
      <main id="main" className="vl-page">
        <Skeleton lines={5} label="Building your review queue" />
      </main>
    );
  }

  if (props.error !== undefined && props.error !== null) {
    return (
      <main id="main" className="vl-page">
        <ErrorState
          title="We couldn't load your review queue"
          onRetry={props.onRetry}
          alternative={{
            label: 'Learn new words instead',
            onClick: () => props.onNavigate('/learn/words/deck'),
          }}
        />
      </main>
    );
  }

  if (cards.length === 0) {
    return (
      <main id="main" className="vl-page">
        <EmptyState
          title="Nothing due right now"
          body={props.queue === null ? undefined : `Next wave: ${nextWave(props.queue.forecast)}.`}
          action={{
            label: 'Learn 5 new words',
            onClick: () => props.onNavigate('/learn/words/deck'),
          }}
        />
      </main>
    );
  }

  if (card === undefined) {
    return (
      <main id="main" className="vl-page">
        <EmptyState
          title="Review done"
          body={`${grades.length} cards reviewed.`}
          action={{ label: 'Back to today', onClick: () => props.onNavigate('/') }}
        />
      </main>
    );
  }

  const spoken = card.modality === 'production' || card.modality === 'minimalPair';

  return (
    <main id="main" className="vl-page vl-review">
      {props.offline && (
        <Banner tone="offline">
          You&apos;re offline. Your grades are saved and will sync when you reconnect.
        </Banner>
      )}

      <header className="vl-review__header">
        <p className="vl-review__progress" aria-live="polite">
          Card {index + 1} of {cards.length}
        </p>
        <span className="vl-review__reason" data-reason={card.reason}>
          {REASON_LABEL[card.reason]}
        </span>
      </header>

      <section className="vl-review__card" aria-label="Review card">
        <p className="vl-review__prompt">{card.prompt}</p>

        {revealed && <p className="vl-review__answer">{card.answer}</p>}

        {!revealed && !spoken && (
          <Button
            onClick={() => {
              setRevealed(true);
            }}
          >
            Show answer
          </Button>
        )}

        {spoken && props.allowSpoken && (
          <MicButton
            state="idle"
            onStart={() => undefined}
            onStop={() => {
              // In the full build the recorder returns a score; graded as correct with a
              // neutral score here so the queue always advances.
              setRevealed(true);
            }}
          />
        )}

        {revealed && (
          <div className="vl-review__grades">
            <Button variant="secondary" onClick={() => commit(false)}>
              Again
            </Button>
            <Button variant="secondary" onClick={() => commit(true)}>
              Hard
            </Button>
            <Button onClick={() => commit(true)}>Good</Button>
          </div>
        )}

        {!revealed && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setHintUsed(true);
              setRevealed(true);
            }}
          >
            I don&apos;t know
          </Button>
        )}
      </section>

      {props.onReport !== undefined && (
        <section className="vl-review__report" aria-label="Report this card">
          {reporting ? (
            <>
              <p className="vl-review__report-prompt" id="vl-report-prompt">
                What&apos;s wrong with it?
              </p>
              <ul className="vl-review__report-reasons" aria-labelledby="vl-report-prompt">
                {DISPUTE_REASON_ORDER.map((reason) => (
                  <li key={reason}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        props.onReport?.(card.itemId, reason);
                        setReported([...reported, card.itemId]);
                        setReporting(false);
                        setRevealed(false);
                        setHintUsed(false);
                        setShownAt(Date.now());
                        track('content_reported', {
                          item: card.itemId,
                          reason,
                          surface: 'review',
                        });
                      }}
                    >
                      {DISPUTE_REASON_LABELS[reason]}
                    </Button>
                  </li>
                ))}
              </ul>
              <Button variant="ghost" size="sm" onClick={() => setReporting(false)}>
                Never mind
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setReporting(true)}>
              Something&apos;s wrong with this card
            </Button>
          )}
        </section>
      )}
    </main>
  );
}

const REASON_LABEL: Record<Card['reason'], string> = {
  mistake: 'Open mistake',
  due: 'Due',
  leech: 'Tricky — new angle',
  new: 'New',
};

function nextWave(forecast: readonly { dayOffset: number; count: number }[]): string {
  const upcoming = forecast.find((day) => day.dayOffset > 0 && day.count > 0);
  if (upcoming === undefined) return 'nothing scheduled this week';
  return `${upcoming.count} cards in ${upcoming.dayOffset} ${upcoming.dayOffset === 1 ? 'day' : 'days'}`;
}

export interface MistakeWorklistProps {
  readonly data: Mistakes | null;
  readonly loading: boolean;
  readonly status: 'open' | 'corrected';
  readonly onStatus: (status: 'open' | 'corrected') => void;
  readonly onPractise: (mistakeId: string) => void;
  readonly onNavigate: (route: string) => void;
  readonly track?: Track | undefined;
}

/** `Uncorrected | Corrected` tabs: a mistake is closable, not a growing pile. */
export function MistakeWorklist(props: MistakeWorklistProps): JSX.Element {
  const track = props.track ?? noTracking;
  const counts = props.data;
  const closed = useRef(new Set<string>());

  // A mistake closing is the product working, and it is server-confirmed: `closedAt`
  // is set by the grader, not by this screen, so the event fires when the list comes
  // back with it rather than when the learner taps anything.
  useEffect(() => {
    for (const mistake of counts?.items ?? []) {
      if (mistake.closedAt === null || closed.current.has(mistake.id)) continue;
      closed.current.add(mistake.id);
      track('mistake_closed', { type: mistake.kind, attempts: mistake.attempts });
    }
  }, [counts, track]);

  // Both counts together, because the product claim is that the pile shrinks: open
  // without corrected would show a backlog and hide whether anything ever closes.
  useEffect(() => {
    if (counts === null) return;
    track('mistake_list_view', { open: counts.openCount, closed: counts.correctedCount });
  }, [counts, track]);

  if (props.loading) {
    return (
      <main id="main" className="vl-page">
        <Skeleton lines={5} label="Loading your mistakes" />
      </main>
    );
  }

  const data = props.data;
  if (data === null) {
    return (
      <main id="main" className="vl-page">
        <ErrorState
          title="We couldn't load your mistakes"
          onRetry={() => props.onNavigate('/review/mistakes')}
        />
      </main>
    );
  }

  return (
    <main id="main" className="vl-page vl-worklist">
      <h1 className="vl-worklist__title">Your mistakes</h1>

      <div className="vl-tabs" role="tablist" aria-label="Mistake status">
        <button
          type="button"
          role="tab"
          aria-selected={props.status === 'open'}
          className="vl-tabs__tab"
          onClick={() => props.onStatus('open')}
        >
          Uncorrected ({data.openCount})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={props.status === 'corrected'}
          className="vl-tabs__tab"
          onClick={() => props.onStatus('corrected')}
        >
          Corrected ({data.correctedCount})
        </button>
      </div>

      {data.items.length === 0 ? (
        <EmptyState
          title={props.status === 'open' ? 'Nothing open' : 'Nothing corrected yet'}
          body={
            props.status === 'open'
              ? 'You have cleared your worklist.'
              : 'Fix an open mistake and it will show up here.'
          }
          action={{ label: 'Back to today', onClick: () => props.onNavigate('/') }}
        />
      ) : (
        <ul className="vl-worklist__list">
          {data.items.map((mistake) => (
            <li key={mistake.id} className="vl-worklist__item" data-severity={mistake.severity}>
              <div className="vl-worklist__body">
                <p className="vl-worklist__original" lang="und">
                  {mistake.original}
                </p>
                {mistake.corrected !== null && (
                  <p className="vl-worklist__corrected">→ {mistake.corrected}</p>
                )}
                <p className="vl-worklist__explanation">{mistake.explanation}</p>
                <p className="vl-worklist__meta">
                  {mistake.kind}
                  {mistake.phoneme !== null && ` · /${mistake.phoneme}/`}
                  {mistake.attempts > 0 && ` · ${mistake.attempts} attempts`}
                  {/* Honest about what it takes to close this. */}
                  {mistake.closeModality !== null && ` · closes with ${mistake.closeModality}`}
                </p>
              </div>
              {props.status === 'open' && (
                <Button
                  size="sm"
                  onClick={() => {
                    track('mistake_practise', { type: mistake.kind, item: mistake.id });
                    props.onPractise(mistake.id);
                  }}
                >
                  Practise
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

export interface ClinicProps {
  readonly data: Clinic | null;
  readonly loading: boolean;
  readonly onPractise: (phoneme: string) => void;
  readonly onNavigate: (route: string) => void;
  readonly track?: Track | undefined;
  /**
   * One sound to work on, when the learner asked for that sound.
   *
   * A clinic that always shows every weak sound cannot answer "fix this one", which is
   * what a mistake in the worklist and a failed phoneme in a report card both ask.
   */
  readonly focus?: string | undefined;
  /**
   * Records the learner saying one word and returns what it scored.
   *
   * Absent means no production practice is offered — which is right for a build with no
   * microphone permission path rather than showing a button that cannot work.
   */
  readonly onPractiseAloud?: ((word: string) => Promise<WordAttempt>) | undefined;
}

/**
 * E4's minimal-pair discrimination drill: hear two, pick which.
 *
 * The clinic showed the contrast as text — "Minimal pairs: /r/ vs /ʎ/" — which is a
 * phoneme symbol a learner cannot practise. This plays one of two real words and asks
 * which it was, which is the task the spec names and the one that trains perception
 * before production.
 *
 * Deliberately not scored and not reported. Discrimination is the scaffolding step: E4
 * puts production practice after it, and only speaking closes a pronunciation mistake
 * (`closesMistake` requires the modality it was failed in). A drill that quietly retired
 * a mistake for hearing a difference would be closing it on the wrong evidence.
 */
function DiscriminationDrill({
  pair,
}: {
  readonly pair: {
    readonly a: string;
    readonly b: string;
    readonly gloss: string;
    readonly audioA: string | null;
    readonly audioB: string | null;
  };
}): JSX.Element | null {
  /** Which of the two is playing, chosen when the learner asks to hear it. */
  const [asked, setAsked] = useState<'a' | 'b' | null>(null);
  const [verdict, setVerdict] = useState<boolean | null>(null);

  // No audio means no drill: two written words with a "which did you hear" question is
  // not a listening task. The words still show above, which is the useful part.
  if (pair.audioA === null || pair.audioB === null) return null;

  const play = (which: 'a' | 'b') => {
    setAsked(which);
    setVerdict(null);
    void playMedia(which === 'a' ? pair.audioA : pair.audioB);
  };

  const choose = (which: 'a' | 'b') => {
    if (asked === null) return;
    setVerdict(which === asked);
  };

  return (
    <div className="vl-clinic__drill">
      {asked === null ? (
        <Button variant="secondary" onClick={() => play(Math.random() < 0.5 ? 'a' : 'b')}>
          Play one of them
        </Button>
      ) : (
        <>
          <p className="vl-clinic__drill-question">Which did you hear?</p>
          <div className="vl-clinic__drill-choices">
            {(['a', 'b'] as const).map((which) => (
              <Button
                key={which}
                variant={verdict === null ? 'secondary' : 'ghost'}
                disabled={verdict !== null}
                onClick={() => choose(which)}
              >
                {pair[which]}
              </Button>
            ))}
          </div>
          {verdict !== null && (
            <>
              {/* The words carry the verdict as well as the colour: a right/wrong
                  distinction made by hue alone fails WCAG 1.4.1. */}
              <p className="vl-drill__verdict" data-correct={verdict}>
                {verdict ? 'Right.' : `Not quite — that was “${pair[asked]}”.`}
              </p>
              <Button variant="ghost" onClick={() => play(Math.random() < 0.5 ? 'a' : 'b')}>
                Again
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * E4's production practice: say the word, and hear what it scored.
 *
 * The discrimination drill above trains perception. This is the half that can clear the
 * mistake: `recordPhonemeOutcomes` on the server closes a pronunciation mistake when the
 * sound comes out right in a scored utterance, and nothing but a spoken attempt satisfies
 * that rule — which is why the drill does not close anything and this can.
 *
 * Three outcomes, all of them said out loud rather than collapsed into a number: a score,
 * a withheld score (declining to score is first-class in this product), and a failure to
 * get as far as scoring at all.
 */
function ProductionPractice({
  word,
  onPractise,
}: {
  readonly word: string;
  readonly onPractise: (word: string) => Promise<WordAttempt>;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState<WordAttempt | null>(null);

  return (
    <div className="vl-clinic__say">
      <Button
        variant="secondary"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setAttempt(null);
          void onPractise(word)
            .then(setAttempt)
            .finally(() => setBusy(false));
        }}
      >
        {busy ? 'Listening…' : `Say “${word}”`}
      </Button>
      {attempt !== null && (
        <p className="vl-clinic__say-result" role="status" data-outcome={attempt.status}>
          {attempt.status === 'scored' && attempt.score !== null
            ? `${Math.round(attempt.score)} out of 100.`
            : (attempt.message ?? 'No score for that one.')}
        </p>
      )}
    </div>
  );
}

/** The pronunciation clinic: bounded, quantified remediation per sound. */
export function PronunciationClinic(props: ClinicProps): JSX.Element {
  const track = props.track ?? noTracking;
  const clinic = props.data;

  // Which sounds are weak, by name. The clinic's whole claim is that remediation is
  // specific and bounded, and an aggregate count could not show that.
  useEffect(() => {
    if (clinic === null) return;
    track('clinic_view', {
      weak_phonemes: clinic.phonemes.map((phoneme) => phoneme.phoneme),
    });
  }, [clinic, track]);

  const total = useMemo(
    () => props.data?.phonemes.reduce((sum, p) => sum + p.estimatedMinutes, 0) ?? 0,
    [props.data],
  );

  if (props.loading) {
    return (
      <main id="main" className="vl-page">
        <Skeleton lines={4} label="Working out your weak sounds" />
      </main>
    );
  }

  const all = props.data?.phonemes ?? [];
  // A focus the learner no longer has a weakness for shows the whole list rather than
  // an empty screen: the sound was fixed, which is good news, not an error.
  const focused = props.focus === undefined ? [] : all.filter((p) => p.phoneme === props.focus);
  const phonemes = focused.length > 0 ? focused : all;

  if (all.length === 0) {
    return (
      <main id="main" className="vl-page">
        <EmptyState
          title="No weak sounds yet"
          body="Finish a speaking lesson and we'll find the sounds worth fixing."
          action={{ label: 'Start a lesson', onClick: () => props.onNavigate('/learn') }}
        />
      </main>
    );
  }

  return (
    <main id="main" className="vl-page vl-clinic">
      <h1 className="vl-clinic__title">
        {focused.length > 0 ? `The sound /${props.focus}/` : 'Pronunciation clinic'}
      </h1>
      <p className="vl-clinic__summary">
        {focused.length > 0
          ? `${focused[0]?.errorCount ?? 0} slips so far · about ${focused[0]?.estimatedMinutes ?? 0} min`
          : `${phonemes.length} ${phonemes.length === 1 ? 'sound' : 'sounds'} · about ${total} min`}
      </p>
      {focused.length > 0 && (
        <Button variant="ghost" onClick={() => props.onNavigate('/review/clinic')}>
          All your sounds
        </Button>
      )}

      <ul className="vl-clinic__list">
        {phonemes.map((phoneme) => (
          <li key={phoneme.phoneme} className="vl-clinic__item">
            <h2 className="vl-clinic__phoneme">
              <code className="vl-ipa">/{phoneme.phoneme}/</code>
            </h2>
            <p className="vl-clinic__errors">
              {phoneme.errorCount} {phoneme.errorCount === 1 ? 'slip' : 'slips'} so far
            </p>
            <p className="vl-clinic__articulation">{phoneme.articulation}</p>
            {phoneme.minimalPairs.length > 0 && (
              <p className="vl-clinic__pairs">
                Minimal pairs: {phoneme.minimalPairs.map(([a, b]) => `/${a}/ vs /${b}/`).join(', ')}
              </p>
            )}
            {/*
              The words behind that contrast, and E4's drill on them.
              
              The line above is phoneme symbols, which is what the screen had: a learner
              cannot practise `/r/ vs /rr/`. These are real words, and the list is empty
              where there is no honest pair — Spanish has merged `ʎ`/`j` for most speakers
              and `β`/`v` are one phoneme, so the drill does not appear rather than
              teaching a distinction the language does not make.
            */}
            {phoneme.pairs.map((pair) => (
              <div key={`${pair.a}-${pair.b}`} className="vl-clinic__pair">
                <p className="vl-clinic__pair-words">
                  <strong>{pair.a}</strong> vs <strong>{pair.b}</strong>
                  <span className="vl-clinic__pair-gloss"> {pair.gloss}</span>
                </p>
                {/*
                  No analytics per answered item. `clinic_drill` already fires when a
                  drill is *started* (the remediation card below), so emitting it again
                  per item would conflate two different things in one metric. Per-attempt
                  data belongs with the production practice that produces real scores.
                */}
                <DiscriminationDrill pair={pair} />
                {/*
                  E4's production practice, on the word that carries the sound being
                  fixed. `minimalPairsFor` returns the contrast expected-first, so `a` is
                  the learner's target and `b` is what they drift toward — asking them to
                  say the drifted-to word would be practising the mistake.
                */}
                {props.onPractiseAloud !== undefined && (
                  <ProductionPractice word={pair.a} onPractise={props.onPractiseAloud} />
                )}
              </div>
            ))}
            {/*
              The progress sparkline E4 asks for. `history` has been in the contract all
              along and was drawn on no platform — here because there was no chart
              primitive, and on both phones because the DTO dropped the field entirely.
            */}
            <Sparkline
              values={phoneme.history.map((entry) => entry.score)}
              label={`Your attempts at /${phoneme.phoneme}/. ${sparklineSummary(
                phoneme.history.map((entry) => entry.score),
                'out of 100',
              )}`}
            />
            <RemediationCard
              phonemes={[phoneme.phoneme]}
              estimatedMinutes={phoneme.estimatedMinutes}
              projectedAccuracyGain={phoneme.projectedAccuracyGain}
              onStart={() => {
                // `before` and `after` are the clinic's own claim about remediation.
                // Only `before` is knowable here; `after` is sent as a present null
                // rather than as the projection, which is a forecast and not a result.
                track('clinic_drill', {
                  phoneme: phoneme.phoneme,
                  before: phoneme.meanScore,
                  after: null,
                });
                props.onPractise(phoneme.phoneme);
              }}
            />
          </li>
        ))}
      </ul>
    </main>
  );
}
